import { Fragment } from "react";
import { Card } from "@/ingot";
import { useTranslation } from "react-i18next";

import type { LinePriceBreakdown } from "@/api/types";

import { Pill } from "@/components/ui/Pill";
import { DEFAULT_LANG } from "@/i18n";
import { useDisplayMoney } from "@/lib/currency";
import { applyVatDisplay, type StorefrontVatDisplay, vatSuffixVariant } from "@/lib/vatDisplay";
import { minorToMajor, moneyFromPair, moneyMapFromPair } from "@/lib/money";
import { cx } from "@/lib/utils";
import { formatWeightKg } from "@/lib/weight";

export interface PriceRowItem {
  label: string;
  value: number;
  tone?: "ok";
}

/**
 * Built-in i18n fallback labels for well-known component keys, used ONLY when
 * the formula declares no admin-authored ``cost_labels[key]``. Lets legacy
 * formulas (laser: area/cut/pierce) keep their localized wording without a
 * data migration; any process whose admin sets a component label overrides it.
 */
const KNOWN_LABEL_I18N: Record<string, string> = {
  area: "price.areaCost",
  cut: "price.cutCost",
  pierce: "price.pierceCost",
  surcharge: "price.finishSurcharge",
  material: "price.materialCost",
  machine: "price.machineCost",
};

/** ``snake_case`` → ``Sentence case`` for unknown component keys. */
function humanizeKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key;
}

/**
 * KAN-744: chyby, jejichž příčinou jsou DATA tenanta (chybějící sazba,
 * nenaceněný materiál, žádná cenová cesta) — žádná úprava konfigurace
 * je nespraví. Zákazník dostane neutrální „není naceněno, kontaktujte
 * nás" místo zavádějícího „upravte konfiguraci".
 */
const AUTHORING_ERROR_CODES = new Set([
  "formula_var_unresolved",
  "formula_var_unit_incompatible",
  "no_pricing_path",
]);

interface Props {
  breakdown: LinePriceBreakdown | undefined;
  isLoading: boolean;
  /** KAN-1703 — díl se právě přepočítává (zákazník změnil interaktivní vstup);
   *  karta místo „Počítám cenu…" řekne „Přepočítávám…". */
  isRecalculating?: boolean;
  errorCode?: string | null;
  /** ``params`` z ApiError — pro admin debug řádek (KAN-744). */
  errorParams?: Record<string, unknown> | null;
  /** Přihlášený admin TOHOTO tenanta (nebo platform admin) — vidí u
   *  chybové karty panel „Admin insight" (KAN-988). */
  isTenantAdmin?: boolean;
  /** UI jazyk — prefix admin deep linku (``/cs/admin/...``). */
  lang?: string;
  locale?: string;
  /** Optional total weight (kg). Computed by the orchestrator from
   *  the process's ``weight_kg`` formula and shown alongside the
   *  line total. ``null`` hides the weight row. */
  weightKg?: number | null;
  /** KAN-1047 — server said this failure still lets the part into the
   *  cart without a price (``params.can_add_unpriced`` on the 422). The
   *  card then offers the way forward instead of "contact us". Passed in
   *  rather than re-derived here so the card and the add-to-cart button
   *  cannot disagree about what the customer may do. */
  canAddUnpriced?: boolean;
  /** KAN-1432 — s DPH, nebo bez? Rozhoduje o tom obchod (komu tenant
   *  prodává + sazba jeho první země dopravy), takže to karta dostane
   *  hotové a sama se nikoho neptá. Výchozí je cena bez DPH. */
  vat?: StorefrontVatDisplay;
  /** KAN-1532 — kontrola vyrobitelnosti pro tenhle výběr BLOKUJE. Cena se
   *  spočítá i tak (plocha × sazba + řez + propaly nezávisí na tom, jestli
   *  jde díl vyrobit), ale číslo je pak nabídka na něco, co si zákazník
   *  koupit nemůže — u dílu s nulovou plochou to bylo 261 Kč vedle červené
   *  cedule „nelze vyrobit". Karta místo částky řekne, na co se čeká. */
  blockedByManufacturability?: boolean;
}

/**
 * Line-item price card for the configurator.
 *
 * Top: a label strip ("CENA ZA KUS · {N} KS") above the unit price
 * hero. Middle: line total + weight row. Bottom: a ``<details>``
 * panel with the per-component breakdown driven by the process
 * formula's own components (+ setup / discount). The collapsed
 * breakdown keeps the primary card focused while the spec stays one
 * click away.
 */
export function PriceBreakdown({
  breakdown,
  isLoading,
  isRecalculating = false,
  errorCode,
  errorParams,
  isTenantAdmin,
  lang,
  locale,
  weightKg,
  canAddUnpriced = false,
  // KAN-1498 — ``appliesVat: true`` je výchozí záměrně: karta bez předané
  // hodnoty se chová jako dosud („bez DPH"). Doložka smí nejvýš přijít
  // pozdě, až se business-info načte; nikdy nesmí zmizet plátci.
  vat = { mode: "net", ratePct: 0, appliesVat: true },
  blockedByManufacturability = false,
}: Props): JSX.Element {
  const { t } = useTranslation("quote");
  // KAN-2189 — cena za kus na haléře (KAN-1900): celé koruny lhaly
  // „28 Kč/ks × 20 = 550 Kč".
  const { format, formatUnit } = useDisplayMoney(locale);

  // KAN-988: „Admin insight" — přihlášený admin téhož tenanta dostane
  // u nenaceněné položky diagnózu VĚTOU, číslovaný postup a odkaz na
  // formulář, kde chybějící hodnota bydlí. Syrový ``error_code`` +
  // ``params`` zůstávají, ale až dole jako technická stopa: do KAN-988
  // byly jediným sdělením a nikomu novému neřekly nic.
  //
  // Klasifikaci NEDĚLÁ tenhle soubor — ``fix_code`` / ``fix_href`` posílá
  // server z ``pricing_dryrun.fix_hint_for_pricing_error``, téže mapy,
  // kterou renderuje dry-run i „Cena procesu". Bez hintu (chyba, kterou
  // žádný tenantský formulář neopraví) se vypíše platformní varianta.
  // Zákazníkům se panel nikdy neukazuje.
  const fixCode =
    typeof errorParams?.fix_code === "string" ? errorParams.fix_code : null;
  const fixHref =
    typeof errorParams?.fix_href === "string" ? errorParams.fix_href : null;
  const rawParams = errorParams
    ? Object.fromEntries(
        Object.entries(errorParams).filter(
          ([key]) => key !== "fix_code" && key !== "fix_href",
        ),
      )
    : null;
  const diagKey = fixCode ?? "platform_side";
  const steps = t(`price.diag.${diagKey}.steps`, {
    returnObjects: true,
  }) as unknown;
  const stepList = Array.isArray(steps) ? (steps as string[]) : [];
  const adminInsight =
    isTenantAdmin && errorCode ? (
      <div
        className="mt-3 rounded-md border border-border bg-surface-2 p-3"
        data-testid="price-admin-insight"
      >
        <Pill tone="accent">{t("price.diag.pill")}</Pill>
        <p className="mt-2 text-sm font-medium text-ink">
          {t(`price.diag.${diagKey}.what`)}
        </p>
        {stepList.length > 0 && (
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-[13px] text-ink-2">
            {stepList.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        )}
        {fixHref && (
          <a
            className="mt-3 inline-flex text-[13px] font-medium text-accent-ink underline"
            data-testid="price-admin-insight-fix"
            href={
              fixHref.startsWith("/admin")
                ? `/${lang || DEFAULT_LANG}${fixHref}`
                : fixHref
            }
          >
            {t(`price.diag.${diagKey}.cta`)}
          </a>
        )}
        <p
          className="mono mt-3 break-all text-[11px] text-ink-3"
          data-testid="price-error-debug"
        >
          {errorCode}
          {rawParams && Object.keys(rawParams).length > 0
            ? ` ${JSON.stringify(rawParams)}`
            : ""}
        </p>
      </div>
    ) : null;

  // KAN-1532 — PRVNÍ větev, přede všemi ostatními: dokud kontrola
  // vyrobitelnosti blokuje, není co nabízet, ať se cena spočítala jakkoli
  // dobře. Tlačítko „Přidat do košíku" je v té chvíli vypnuté, takže částka
  // vedle něj je slib, který obchod nemůže splnit.
  if (blockedByManufacturability) {
    return (
      <Card
        padded
        className="border-warn-border bg-warn-bg"
        data-testid="price-breakdown-blocked"
      >
        <p className="text-sm text-ink-2">{t("price.blockedByManufacturability")}</p>
      </Card>
    );
  }

  // KAN-1140 — „jen na poptávku" jde stejnou cestou jako pád ceny, ale
  // NENÍ to pád a zákazníkovi se to tak říct nesmí: „zatím to neumíme
  // nacenit" by z rozhodnutí dílny udělalo omluvu za nedodělek. Kartu
  // proto rozlišuje kód, ne příznak — příznak dál rozhoduje jen o tom,
  // jestli se smí do košíku. Musí být PŘED větví ``canAddUnpriced``,
  // která by ji jinak spolkla.
  if (errorCode === "quote_only_selection") {
    return (
      <Card
        padded
        className="border-warn-border bg-warn-bg"
        data-testid="price-breakdown-quote-only"
      >
        <p className="text-sm font-medium text-ink">
          {t("price.quoteOnlyBadge")}
        </p>
        <p className="mt-1 text-sm text-ink-2">{t("price.quoteOnly")}</p>
      </Card>
    );
  }

  // KAN-1047 — když BE u téhle chyby řekl, že díl smí do košíku i bez
  // ceny, je „kontaktujte nás" špatná rada: pod kartou je tlačítko.
  // Rozhoduje TÝŽ příznak jako u tlačítka, ne vlastní seznam kódů —
  // jinak by karta slibovala cestu, kterou tlačítko nenabídne.
  if (canAddUnpriced) {
    return (
      <Card
        padded
        className="border-warn-border bg-warn-bg"
        data-testid="price-breakdown-not-priced-addable"
      >
        <p className="text-sm text-ink-2">{t("price.notPricedAddable")}</p>
        {adminInsight}
      </Card>
    );
  }

  // KAN-1903 — the shop's per-line cap. Not a pricing failure: the price
  // preview refuses with the cart's own code and params, so the customer
  // learns the limit while choosing the quantity, not at "Add to cart".
  if (errorCode === "line_qty_exceeds_max") {
    const qty = Number(errorParams?.quantity);
    const cap = Number(errorParams?.max_quantity);
    return (
      <Card
        padded
        className="border-warn-border bg-warn-bg"
        data-testid="price-breakdown-qty-over-cap"
      >
        <p className="text-sm text-ink-2">
          {t("price.qtyOverCap", {
            quantity: Number.isFinite(qty) ? qty.toLocaleString(locale) : "",
            max_quantity: Number.isFinite(cap) ? cap.toLocaleString(locale) : "",
          })}
        </p>
      </Card>
    );
  }

  if (errorCode === "pricing_unavailable") {
    return (
      <Card padded className="border-warn-border bg-warn-bg">
        <p className="text-sm text-ink-2">{t("price.unavailable")}</p>
        {adminInsight}
      </Card>
    );
  }

  // KAN-744: datové/autorské chyby — viz ``AUTHORING_ERROR_CODES``.
  if (errorCode && AUTHORING_ERROR_CODES.has(errorCode)) {
    return (
      <Card
        padded
        className="border-warn-border bg-warn-bg"
        data-testid="price-breakdown-not-priced"
      >
        <p className="text-sm text-ink-2">{t("price.notPriced")}</p>
        {adminInsight}
      </Card>
    );
  }

  // Any OTHER pricing error must surface as an error state — never fall through
  // to the loading branch below. ``breakdown`` is undefined on a failed request,
  // so without this branch a 422 with an unknown code — or a 5xx / network
  // error — would spin "Počítám cenu…" forever instead of telling the customer
  // the price couldn't compute.
  if (errorCode) {
    return (
      <Card
        padded
        className="border-danger-border bg-danger-bg"
        data-testid="price-breakdown-error"
      >
        <p className="text-sm text-ink-2">{t("price.error")}</p>
        {adminInsight}
      </Card>
    );
  }

  if (isLoading || !breakdown) {
    return (
      <Card padded data-testid="price-breakdown-skeleton">
        <p className="text-sm text-ink-3">
          {isRecalculating ? t("price.recalculating") : t("price.loading")}
        </p>
      </Card>
    );
  }

  const discountPct = Number(breakdown.discount_pct) || 0;
  const setupFee = moneyFromPair(
    breakdown.setup_fee_minor,
    breakdown.setup_fee_czk,
    breakdown.currency_code,
  );
  // KAN-1959 — jednorázové výstupy vzorce (``per_batch``, např. „Příprava
  // úlohy" u FDM) jsou UVNITŘ ``setup_fee``: každý dostane vlastní řádek
  // a „Příprava (jednorázově)" ukáže jen zbytek (poplatek za přípravu
  // procesu), aby se nic nesečetlo dvakrát.
  const batchRows = (breakdown.formula_outputs ?? [])
    .filter((o) => o.mode === "per_batch")
    .map((o) => ({
      label: t("price.batchOutputOnce", {
        name:
          (o.display_name ?? o.name)?.trim() ||
          t("price.batchOutputFallback", { defaultValue: "Příprava úlohy" }),
        defaultValue: "{{name}} (jednorázově)",
      }),
      // Řádek ``per_batch`` vznikl až s minor penězi — nese je vždycky.
      value: minorToMajor(o.amount_minor, breakdown.currency_code),
    }));
  const batchTotal = batchRows.reduce((sum, r) => sum + r.value, 0);
  const remainingSetupFee = setupFee - batchTotal;
  const showSetup = setupFee > 0;
  // Per-piece rows multiply by qty; setup is added flat once (RAW).
  // perPieceTotal = unit_price × qty; line_total = perPieceTotal + setup.
  // KAN-284 b2 — every money slot arrives in two spellings during the
  // transition; the minor one wins because it alone can carry sub-unit
  // precision. Frozen snapshots predating it fall back to the whole-unit half.
  const cur = breakdown.currency_code;
  const unitPrice = moneyFromPair(
    breakdown.unit_price_minor,
    breakdown.unit_price_czk,
    cur,
  );
  const lineTotal = moneyFromPair(
    breakdown.line_total_minor,
    breakdown.line_total_czk,
    cur,
  );

  // KAN-1432 — obchod pro koncové zákazníky ukazuje konečnou cenu. Sazba je
  // náhled první aktivní země dopravy, tutéž počítá souhrn košíku; bez ní
  // zůstává cena bez DPH. Rozpad níž je vždycky bez DPH a říká to.
  const grossUp = (amount: number) => applyVatDisplay(amount, vat);
  // KAN-1498 — tři stavy, ne dva: neplátce nemá cenu „bez DPH", má prostě
  // cenu. Viz ``vatSuffixKey``.

  const perPieceTotal = unitPrice * breakdown.quantity;
  // Hero kus-cena amortizuje setup přes všechny kusy → line_total / qty.
  // Detail panel níže ukazuje surovou jednotkovou cenu v "Součet za díl".
  const heroUnitPrice = breakdown.quantity > 0 ? lineTotal / breakdown.quantity : unitPrice;

  // Per-piece values post markup/delivery/discount — these sum to
  // ``unit_price_czk`` (±1 CZK rounding per component) so the customer
  // can manually reconcile rows × qty + setup → line_total. Falls
  // back to the raw ``cost_breakdown`` for snapshots that predate the
  // ``effective_breakdown_czk`` field on the engine response.
  const eff = moneyMapFromPair(
    breakdown.effective_breakdown_minor,
    breakdown.effective_breakdown_czk,
    cur,
  ) as Record<string, number>;
  const raw = breakdown.cost_breakdown ?? {};
  const labels = breakdown.cost_labels ?? {};

  // Row label precedence: admin-authored ``cost_labels`` (single source of
  // truth, set in the platform-admin formula editor) → built-in i18n for
  // known keys → humanized raw key. So the breakdown always matches the
  // process's actual formula components instead of laser-hardcoded rows.
  const rowLabel = (key: string): string => {
    const authored = labels[key];
    if (authored && authored.trim()) return authored;
    const i18nKey = KNOWN_LABEL_I18N[key];
    return i18nKey ? t(i18nKey) : humanizeKey(key);
  };

  // Component keys come from the engine breakdown, in formula order. Prefer
  // the post-markup ``effective_breakdown_czk`` (what the customer reconciles
  // against the unit price); fall back to raw ``cost_breakdown``. Non-numeric
  // values (sandbox "calc_failed" markers) are skipped.
  const componentKeys = (
    Object.keys(eff).length > 0 ? Object.keys(eff) : Object.keys(raw)
  ).filter((k) => typeof (eff[k] ?? raw[k]) === "number");

  // Named per-piece outputs of the formula (admin-authored in the graph
  // editor). When any output is named they ARE the breakdown rows — the admin
  // picks the row granularity by naming outputs; otherwise we fall through to
  // the component breakdown so flat / unnamed formulas render unchanged.
  const namedOutputs = (breakdown.formula_outputs ?? []).filter(
    (o) => o.mode === "per_piece",
  );
  const hasNamedOutputs = namedOutputs.some((o) => o.name && o.name.trim());

  let rows: PriceRowItem[];
  if (hasNamedOutputs) {
    rows = namedOutputs.map((o) => ({
      label:
        (o.display_name ?? o.name)?.trim() ||
        t("price.unitPriceFallback", { defaultValue: "Cena za kus" }),
      value: o.amount_czk,
    }));
  } else {
    // Generic per-process breakdown driven by the formula's own components.
    // A snapshot with no component map (pre-``cost_breakdown`` era) renders
    // no component rows — the totals above stay authoritative.
    rows = componentKeys.map((key) => ({
      label: rowLabel(key),
      value: Number(eff[key] ?? raw[key]),
    }));
  }

  // KAN-1036 — „Rozpad ceny" se nabízí, jen když má co rozpadnout.
  // Formule s JEDINÝM pojmenovaným výstupem (u 3D tisku typicky „Tisk
  // dílu") a bez přípravy a slevy vyrobí panel o jednom řádku, který
  // opakuje kus-cenu nad ním — zákazník rozklikne příslib rozpadu na
  // materiál / stroj / marži a najde tutéž částku podruhé. Prázdný
  // ``cost_breakdown`` (a tedy nula řádků) dopadl ještě hůř: panel se
  // otevřel do prázdna. Rozpad, který nic nerozpadá, se nekreslí.
  const hasBreakdownToShow = rows.length > 1 || showSetup || discountPct > 0;

  return (
    <Card padded={false} elevation="raised" data-testid="price-breakdown">
      <div className="px-5 pt-4">
        <p className="mono text-[10px] uppercase tracking-[0.1em] text-ink-3">
          {/* KAN-1522 — „Cena za kus" znamenala na jedne obrazovce dve
              ruzne veci: tady s ROZPUSTENOU pripravou
              (``lineTotal / quantity``), v rozpadu o kus niz BEZ ni.
              Aritmeticky obe sedi, ale laik vidi u tehoz slova dve
              cisla (410 vs 260). Kdyz se priprava uctuje, hlavicka to
              rekne. */}
          {t(
            showSetup ? "price.unitPriceHeaderWithSetup" : "price.unitPriceHeader",
            { count: breakdown.quantity },
          )}
        </p>
        <p className="mt-2 flex items-baseline gap-2 text-ink">
          <span
            className="text-4xl font-semibold tabular-nums tracking-tight"
            data-testid="price-unit-value"
          >
            {formatUnit(grossUp(heroUnitPrice))}
          </span>
          <span className="text-sm text-ink-3">
            {t(`price.unitPriceSuffix${vatSuffixVariant(vat)}`)}
          </span>
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-4 border-t border-border px-5 py-3 text-sm">
        <div>
          <p className="text-[11px] text-ink-3">{t("price.lineTotal")}</p>
          <p
            className="mono mt-0.5 text-base font-medium tabular-nums text-ink"
            data-testid="price-line-total"
          >
            {format(grossUp(lineTotal))}
          </p>
        </div>
        {weightKg !== null && weightKg !== undefined && (
          <div className="text-right">
            <p className="text-[11px] text-ink-3">{t("price.weight")}</p>
            <p className="mono mt-0.5 text-base font-medium tabular-nums text-ink">
              {formatWeightKg(weightKg, locale)}
            </p>
          </div>
        )}
      </div>

      {hasBreakdownToShow && (
      <details className="border-t border-border px-5 py-3" data-testid="price-breakdown-details">
        <summary className="mono flex cursor-pointer list-none items-center justify-between text-[10px] uppercase tracking-[0.1em] text-ink-3 [&::-webkit-details-marker]:hidden">
          <span>
            {t("price.breakdownTitle")}
            {vat.mode === "gross" ? ` · ${t("price.breakdownNet")}` : ""}
          </span>
          <span aria-hidden="true" className="text-ink-3">
            ⌄
          </span>
        </summary>
        <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-sm">
          {rows.map((r) => (
            <PriceRow
              key={r.label}
              label={r.label}
              value={r.value}
              tone={r.tone}
              locale={locale}
            />
          ))}
          {discountPct > 0 && (
            <>
              {/* KAN-1834 — řádky výše sleva UŽ obsahují (jsou po slevě,
                  sčítají se do ceny za kus). Sleva mezi položkami a součtem
                  vypadala jako další odečet — kdo ho provedl, dostal
                  o 12 % míň, než zákazník zaplatí. Je to informace. */}
              <dt className="col-span-2 flex items-center gap-2 text-ok">
                <Pill tone="ok">−{discountPct}%</Pill>
                <span>{t("price.discount", { pct: discountPct })}</span>
              </dt>
            </>
          )}
          {showSetup && (
            <>
              {/* KAN-1522 — mezisoucty se kresli, jen kdyz neco pridavaji.
                  Pri JEDNOM radku formule je „Soucet za dil" tentyz
                  udaj jako ten radek, a pri qty 1 je „Cena za pozadovany
                  pocet kusu" tentyz udaj jako soucet za dil. U 1 ks
                  a jednoradkove formule stalo v rozpadu TRIKRAT totez
                  cislo (260 / 260 / 260) — meereno na nabidce test5. */}
              {rows.length > 1 && (
                <>
                  <dt className="col-span-2 mt-2 border-t border-border pt-2 text-[11px] text-ink-3">
                    {t("price.perPiece", { defaultValue: "Součet za díl" })}
                  </dt>
                  <dd
                    className="mono text-right tabular-nums text-ink-2"
                    style={{ gridColumn: "2" }}
                  >
                    {format(unitPrice)}
                  </dd>
                </>
              )}
              {breakdown.quantity > 1 && (
                <>
                  <dt className="text-ink-2">
                    {t("price.priceForQty", {
                      qty: breakdown.quantity,
                      defaultValue: "Cena za požadovaný počet kusů",
                    })}
                  </dt>
                  <dd className="mono text-right tabular-nums text-ink-2">
                    {format(perPieceTotal)}
                  </dd>
                </>
              )}
              {batchRows.map((r) => (
                <Fragment key={r.label}>
                  <dt className="text-ink-2">{r.label}</dt>
                  <dd className="mono text-right tabular-nums text-ink">
                    + {format(r.value)}
                  </dd>
                </Fragment>
              ))}
              {(batchRows.length === 0 || remainingSetupFee > 0) && (
                <>
                  <dt className="text-ink-2">
                    {t("price.setupFeeOnce", {
                      defaultValue: "Příprava (jednorázově)",
                    })}
                  </dt>
                  <dd className="mono text-right tabular-nums text-ink">
                    + {format(batchRows.length === 0 ? setupFee : remainingSetupFee)}
                  </dd>
                </>
              )}
              <dt className="col-span-2 mt-1 border-t border-border pt-2 text-[11px] font-medium uppercase tracking-wide text-ink-2">
                {t("price.lineTotalSummary", {
                  defaultValue: "Celkem za sérii",
                })}
              </dt>
              <dd
                className="mono text-right text-base font-semibold tabular-nums text-ink"
                style={{ gridColumn: "2" }}
              >
                {format(lineTotal)}
              </dd>
            </>
          )}
        </dl>
      </details>
      )}
    </Card>
  );
}

function PriceRow({
  label,
  value,
  locale,
  tone,
}: {
  label: string;
  value: number;
  locale?: string;
  tone?: "ok";
}): JSX.Element {
  const { format } = useDisplayMoney(locale);
  return (
    <>
      <dt className={cx("text-ink-2", tone === "ok" && "text-ok")}>{label}</dt>
      <dd
        className={cx(
          "mono text-right tabular-nums text-ink",
          tone === "ok" && "text-ok",
        )}
      >
        {format(value)}
      </dd>
    </>
  );
}
